import { RecipientListApiResponse } from "@features/communication/lists/types";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import { communicationApiClient } from "@shared/api/clients/apiClientFactory";
import { PAGE_SIZE } from "@shared/utils/constants";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";

const postRecipientList = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrl("communication", "recipient-lists");
  const response = await communicationApiClient.post(url, payload);
  return response.data;
};

const fetchConfigCategoryJson = async (
  config: string,
  remoteUtilityId: string,
  configLevel: string,
  codeList?: any
): Promise<ConfigData[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "onboarding",
    "config-map",
    [config],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", configLevel);
      if (codeList) {
        codeList.forEach((code) => {
          query.push("code_list", code);
        });
      }
    }
  );

  const response = await communicationApiClient.get(url);
  return transformJsonToObject<ConfigData[]>(response.data.result);
};

export const fetchConsumerData = async (
  params: FetchConsumerDataParams
): Promise<PaginatedConsumerData> => {
  const {
    remoteUtilityId,
    page,
    filterType,
    categoryType,
    categoryCodes = [],
    territoryType,
    territoryCodes = [],
  } = params;

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "fetch-consumer-data",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", true);
      query.push("page", page);
      query.push("limit", PAGE_SIZE);

      if ((filterType && categoryType) || territoryType) {
        query.push("filter_type", filterType);
      }

      if (filterType === "CATEGORY") {
        if (categoryType) query.push("category_type", categoryType);
        categoryCodes.forEach((code) => {
          query.push("category_code", code);
        });
      }

      if (filterType === "TERRITORY") {
        if (territoryType) query.push("territory_type", territoryType);
        territoryCodes.forEach((code) => {
          query.push("territory_code", code);
        });
      }
    }
  );

  const response = await communicationApiClient.get(url);
  return transformJsonToObject<PaginatedConsumerData>(response.data);
};

const patchRecipientList = (id: string | number, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "communication",
    "recipient-lists",
    [id]
  );

  return communicationApiClient.patch(url, payload).then((res) => res.data);
};

export const fetchRecipientListDetail = (
  remoteUtilityId: string | number,
  listId: string | number
): Promise<PaginatedRecipientListData> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "recipient-lists",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("list_id", listId);
    }
  );

  return communicationApiClient
    .get(url)
    .then((res) => transformJsonToObject<PaginatedRecipientListData>(res.data));
};

const getRecipientLists = async (
  utilityId: string,
  filters: {
    recipientType?: string; // Made optional
    filterType?: string;
    isActive?: boolean[];
    search_data?: string;
    page?: number;
    limit?: number;
    userId?: string; // for listOwnership
  }
): Promise<RecipientListApiResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "recipient-lists",
    (query) => {
      // Required params
      query.push("remote_utility_id", utilityId.toString());

      // Only add recipient_type if it's provided in filters
      if (filters.recipientType) {
        query.push("recipient_type", filters.recipientType);
      }

      // Pagination
      if (filters.page) query.push("page", filters.page.toString());
      if (filters.limit) query.push("limit", filters.limit.toString());

      // Filter params
      if (filters.filterType) {
        query.push("filter_type", filters.filterType);
      }

      // Status filter - send as comma-separated values
      if (filters.isActive && filters.isActive.length > 0) {
        query.push(
          "is_active",
          filters.isActive.map((s) => s.toString()).join(",")
        );
      }

      // Search param
      if (filters.search_data) {
        query.push("search_data", filters.search_data);
      }

      // User ownership
      if (filters.userId) {
        query.push("user_id", filters.userId.toString());
      }
    }
  );
  const response = await communicationApiClient.get(url);
  return response.data;
};

const exportRecipientLists = async (
  utilityId: string,
  filters: {
    recipientType?: string;
    filterType?: string;
    isActive?: boolean[];
    search_data?: string;
    page?: number;
    limit?: number;
    userId?: string;
  }
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "recipient-lists",
    (query) => {
      // Required params
      query.push("remote_utility_id", utilityId.toString());

      // Only add recipient_type if it's provided in filters
      if (filters.recipientType) {
        query.push("recipient_type", filters.recipientType);
      }

      // Filter params (no pagination for export)
      if (filters.filterType) {
        query.push("filter_type", filters.filterType);
      }

      // Status filter - send as comma-separated values
      if (filters.isActive && filters.isActive.length > 0) {
        query.push(
          "is_active",
          filters.isActive.map((s) => s.toString()).join(",")
        );
      }

      // Search param
      if (filters.search_data) {
        query.push("search_data", filters.search_data);
      }

      // User ownership
      if (filters.userId) {
        query.push("user_id", filters.userId.toString());
      }

      // Export format
      query.push("export_format", "csv");
    }
  );
  
  const response = await communicationApiClient.get(url);
  
  return response.data;
};

// Create recipient list
const createRecipientList = async (listData: {
  remote_utility_id: string;
  name: string;
  recipient_type: "CONSUMER" | "TECHNICIAN" | "BUSINESS";
  description: string;
  tags: string[];
}) => {
  const url = ApiEndpoints.createUrl("communication", `recipient-lists`);
  const response = await communicationApiClient.post(url, {
    ...listData,
    recipient_type: listData.recipient_type.toUpperCase(),
  });
  return response.data.result;
};
const toggleListStatus = async (listId: number, isActive: boolean) => {
  const url = ApiEndpoints.createUrl(
    "communication",
    `recipient-lists/${listId}`
  );
  const response = await communicationApiClient.patch(url, {
    is_active: isActive,
  });
  return response.data;
};

const downloadContactList = async (listId: number): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    `download/list-contacts/${listId}`
  );
  const response = await communicationApiClient.get(url);
  return response.data;
};

export const uxListsApi = {
  postRecipientList,
  fetchConfigCategoryJson,
  fetchConsumerData,
  patchRecipientList,
  fetchRecipientListDetail,
  downloadContactList,
  createRecipientList,
  getRecipientLists,
  toggleListStatus,
  exportRecipientLists,
};
